import React from "react";
import { Col } from "react-bootstrap";
import { FormikProps } from "formik";
import { useParams } from "react-router-dom";
import {
  IControlStyles,
  PageContent,
  useControlStyles,
} from "../../../assets/layouts";
import { SortDirection, SortModel } from "../../../assets/utils/lib/searchDto";
import Form from "../../../assets/vfx/form/form";
import { PushNotificationService } from "./pushNotificationService";
import { Constants } from "../../../assets/utils/lib/constants";
import { GRID_ACTION_TYPES, SetState } from "../../../assets/types";

import { PushNotificationLookupProps } from "./pushNotificationForm";
import {
  RequestPushNotificationGridDto,
  SearchRequestPushNotificationDto,
} from "../../../services/dto";
import { ControlDataGridColumnProps } from "../../../assets/vfx/controls/components/datagrid/controlDataGrid";
import {
  createActionColumn,
  createLayoutOptions,
} from "../../../assets/configuration/gridConfig";
import { SearchContainer } from "../../../assets/vfx/search/searchContainer";
import { VfxLib } from "../../../assets/vfx";
import Control from "../../../assets/vfx/controls/control";
import { useCommunity } from "../../../context/communityContext";

interface PushNotificationPageProps {
  controlStyles?: IControlStyles;
  onCustomEvent?: (actionType: string, dataId: string) => void;
  lookups?: PushNotificationLookupProps;
  setCommunity: SetState<string | undefined>;
  setNotificationType: SetState<string | undefined>;
  communityId?: string;
  notificationTypeId?: string;
  hideTitle?: boolean;
}

export interface PushNotificationSearchProps {
  communityId: string;
  notificationTypeId?: string;
}

const DATA_ID_FIELD = "id";

export const isNewRequest = (status: string | undefined): boolean => {
  return ["new-request", "New"].includes(status ?? "");
};

const gridColumns: ControlDataGridColumnProps[] = [
  {
    data: DATA_ID_FIELD,
    title: "ID",
    visible: false,
    columnType: "number",
    alignment: "right",
    sortable: false,
  },
  {
    data: "scheduledDate",
    title: "Scheduled",
    visible: true,
    columnType: "datetime",
    alignment: "left",
    width: "10%",
  },
  {
    data: "notificationType",
    title: "Type",
    visible: true,
    columnType: "text",
    alignment: "left",
    width: "10%",
  },
  {
    data: "status",
    title: "Status",
    visible: true,
    columnType: "text",
    alignment: "left",
    width: "10%",
  },
  {
    data: "title",
    title: "Title / Message",
    visible: true,
    columnType: "custom",
    alignment: "left",
    sortable: false,
    width: "45%",
    customCell: (_title: string, rowData: RequestPushNotificationGridDto) => (
      <div>
        <div className="fw-semibold">{rowData.title}</div>
        <div className="small text-muted">{rowData.message}</div>
      </div>
    ),
  },
];

const defaultSortBy: SortModel = {
    field: "scheduledDate",
    direction: SortDirection.Descending,
};

const defaultSearchQuery: SearchRequestPushNotificationDto = {
  communityId: Constants.emptyGuid,
  notificationTypeId: undefined,
  pageSize: 10,
  pageNumber: 1,
  sortBy: defaultSortBy, 
};

export const PushNotificationPage = (props: PushNotificationPageProps) => {
  const { communityId: paramCommunityId } = useParams();
  const { communityId } = useCommunity();
  const [formResetKey, setFormResetKey] = React.useState<number>(0);

  const resolvedCommunityId: string | undefined =
    paramCommunityId ?? communityId ?? props.communityId ?? undefined;

  const [searchQuery, setSearchQuery] =
    React.useState<SearchRequestPushNotificationDto>(defaultSearchQuery);

  const styles: IControlStyles = props.controlStyles ?? useControlStyles();

  React.useEffect(() => {
    if (!resolvedCommunityId) return;

    setSearchQuery((prev) =>
      prev.communityId === resolvedCommunityId
        ? prev
        : {
          ...prev,
          communityId: resolvedCommunityId,
          pageNumber: 1,
        }
    );

    if (!paramCommunityId) {
      props.setCommunity(resolvedCommunityId);
    }
  }, [resolvedCommunityId, paramCommunityId]);

  const pushNotificationGridQuery =
    PushNotificationService.gridQuery(searchQuery);
  const isGridLoading =
    pushNotificationGridQuery.isLoading || pushNotificationGridQuery.isFetching;

  const pushNotifications: RequestPushNotificationGridDto[] =
    pushNotificationGridQuery.data?.result ?? [];

  const hasData: boolean = pushNotifications.length > 0;
  const actionColumn = createActionColumn(styles, DATA_ID_FIELD, []);
  const layoutOptions = createLayoutOptions(hasData);

  const getUpdatedSearchQuery = (
    values: SearchRequestPushNotificationDto
  ): SearchRequestPushNotificationDto => ({
    ...defaultSearchQuery,
    communityId:
      values.communityId && values.communityId !== Constants.emptyString
        ? values.communityId
        : resolvedCommunityId ?? defaultSearchQuery.communityId,
    notificationTypeId:
      values.notificationTypeId &&
        values.notificationTypeId !== Constants.emptyString
        ? values.notificationTypeId
        : undefined,
  });

  const applySearch = (updated: SearchRequestPushNotificationDto): void => {
    setSearchQuery(updated);

    if (updated.communityId && updated.communityId !== Constants.emptyGuid) {
      props.setCommunity(updated.communityId);
    }
    props.setNotificationType(updated.notificationTypeId);
  };

  const handleSearch = (values: SearchRequestPushNotificationDto): void => {
    applySearch(getUpdatedSearchQuery(values));
  };

  const handleNotificationTypeChange = (
    formik: FormikProps<SearchRequestPushNotificationDto>,
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const updated: SearchRequestPushNotificationDto = {
      ...formik.values,
      notificationTypeId: event.target.value,
    };

    applySearch(getUpdatedSearchQuery(updated));
  };

  const handleClear = (): void => {
    const preservedCommunityId = resolvedCommunityId ?? searchQuery.communityId;

    setSearchQuery((prev) => ({
      ...defaultSearchQuery,
      communityId: preservedCommunityId ?? prev.communityId,
      sortBy: prev.sortBy,
      pageSize: prev.pageSize ?? 10,
    }));

    if (preservedCommunityId && preservedCommunityId !== Constants.emptyGuid) {
      props.setCommunity(preservedCommunityId);
    }
    props.setNotificationType(undefined);
    setFormResetKey((k) => k + 1);
  };

  const handleNew = (): void | undefined =>
    props.onCustomEvent?.(GRID_ACTION_TYPES.NEW, Constants.emptyGuid);

  const renderCommunityDropdown = () => (
    <Col lg={4}>
      <Form.Dropdown
        name="communityId"
        label="Community"
        blankOption={{
          key: Constants.emptyGuid,
          display: "Select Community",
          value: Constants.emptyString,
        }}
        options={props.lookups?.communities}
      />
    </Col>
  );

  const renderNotificationTypeDropdown = (
    formik: FormikProps<SearchRequestPushNotificationDto>
  ) => (
    <Col
      lg={4}
      className={paramCommunityId || communityId ? "ms-0" : "ms-2"}
    >
      <Form.Dropdown
        name="notificationTypeId"
        label="Notification Type"
        blankOption={{
          key: Constants.emptyGuid,
          display: "Select Notification Type",
          value: Constants.emptyString,
        }}
        options={props.lookups?.notificationType}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          handleNotificationTypeChange(formik, event)
        }
      />
    </Col>
  );

  const renderSearchContainer = () => {
    if (paramCommunityId || communityId) {
      return (
        <SearchContainer
          key={formResetKey}
          initialValues={{
            notificationTypeId:
              searchQuery.notificationTypeId ?? Constants.emptyString,
          }}
          onSearch={handleSearch}
          onClear={handleClear}
          enableReinitialize
        >
          {(formik: FormikProps<SearchRequestPushNotificationDto>) =>
            renderNotificationTypeDropdown(formik)
          }
        </SearchContainer>
      );
    }

    return (
      <SearchContainer
        key={formResetKey}
        initialValues={{
          communityId: searchQuery.communityId ?? Constants.emptyGuid,
          notificationTypeId:
            searchQuery.notificationTypeId ?? Constants.emptyString,
        }}
        onSearch={handleSearch}
        onClear={handleClear}
        enableReinitialize
      >
        {(formik: FormikProps<SearchRequestPushNotificationDto>) => (
          <>
            {renderCommunityDropdown()}
            {renderNotificationTypeDropdown(formik)}
          </>
        )}
      </SearchContainer>
    );
  };

  const content = (
    <>
        <Control.DataGrid
          dataId={DATA_ID_FIELD}
          cols={gridColumns}
          actionCol={{
            ...actionColumn,
            width: "200px",
            actionButtons: (row: RequestPushNotificationGridDto) => {
              const gridActionButtons = VfxLib.datagridActionButtons({
                datagridStyles: styles,
                displayIconOnly: true,
              });
              const type: string = (row.notificationType ?? "").trim();
              const canCopy: boolean = ["Information", "Urgent"].includes(type);
              const canEdit: boolean = isNewRequest(row.status) && !["Poll", "Feedback", "Survey"].includes(type);
              const buttons = [];
              if (canCopy) buttons.push(gridActionButtons.copy);
              if (canEdit) buttons.push(gridActionButtons.edit);
              buttons.push(gridActionButtons.view);
              buttons.push(gridActionButtons.delete);
              return buttons;
            },
          }}
          layoutOptions={layoutOptions}
          onCustomEvent={props.onCustomEvent}
          data={pushNotifications}
          totalRecords={
            pushNotificationGridQuery?.data?.totalSize ??
            pushNotificationGridQuery?.data?.result?.length ??
            0
          }
          query={searchQuery}
          loading={isGridLoading}
          onQueryChange={(patch) =>
            setSearchQuery((query) => ({ ...query, ...patch }))
          }
          uiOptions={{
            title: props.hideTitle ? undefined : "Push Notifications",
            filterContent: renderSearchContainer(),
            primaryAction: {
              text: "Add Push Notification",
              icon: "bi bi-plus-lg",
              onClick: handleNew,
            },
          }}
        />
    </>
  );

  return props.hideTitle ? (
    content
  ) : (
          <PageContent hideHeader>{content}</PageContent>
  );
};
